import { Component, ChangeDetectionStrategy, computed, input, signal } from '@angular/core';
import { MaisToolCall } from '../../../services/chat-mais.service';

let nextInstanceId = 0;

@Component({
  selector: 'app-ai-thinking-card',
  templateUrl: './ai-thinking-card.component.html',
  styleUrls: ['./ai-thinking-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AiThinkingCardComponent {
  toolCalls = input<MaisToolCall[]>([], { alias: 'toolCalls' });
  // Whether the turn's answer text has started arriving yet (item().clCompChat_displayBody
  // non-empty) - the signal that tool-use is conceptually "done", since MAIS's toolCalls[].state
  // has only ever been observed as 'output-available', even on the very first chunk of a turn,
  // so it can't tell us that on its own.
  hasAnswerText = input<boolean>(false, { alias: 'hasAnswerText' });
  // item().clCompChat_aiStreaming - if the turn already finished (or aborted) with no answer
  // text at all, treat it the same as "settled" rather than spinning forever.
  streaming = input<boolean>(false, { alias: 'streaming' });

  // null = no manual interaction yet, so `expanded` auto-tracks settled(); true/false = user has
  // clicked the toggle at least once, which pins the state regardless of later settled() changes.
  private manualOverride = signal<boolean | null>(null);

  settled = computed(() => this.hasAnswerText() || !this.streaming());
  expanded = computed(() => this.manualOverride() ?? !this.settled());

  // Unique per instance so aria-controls/id don't collide when several AI replies are rendered
  // in the same message list.
  readonly listId = `ai-thinking-list-${nextInstanceId++}`;

  toggle(): void {
    this.manualOverride.set(!this.expanded());
  }

  // Only the last tool call spins, and only while the turn is still actively working with no
  // answer text yet - every earlier tool call (and every tool call once settled) reads as done.
  isRowSpinning(toolCall: MaisToolCall): boolean {
    const calls = this.toolCalls();
    return !this.settled() && calls[calls.length - 1] === toolCall;
  }

  rowLabelKey(toolCall: MaisToolCall): string {
    return this.isKnowledgeBaseTool(toolCall) ? 'cl_chat.aiSearchingKnowledgeBase' : 'cl_chat.aiUsingTool';
  }

  rowLabelParams(toolCall: MaisToolCall): { detail: string } {
    const query = typeof toolCall.input?.['query'] === 'string' ? (toolCall.input['query'] as string) : undefined;
    return { detail: query ?? toolCall.toolName };
  }

  private isKnowledgeBaseTool(toolCall: MaisToolCall): boolean {
    return !!toolCall.toolName && toolCall.toolName.includes('knowledge_base');
  }
}
